import angular from 'angular';

import csmAlert from '../../ui/alert/alert';
import csmBusyScreen from '../../ui/busyScreen/busyScreen';

export default angular
  .module('csm.util.CSMHttp', [csmAlert, csmBusyScreen])
  .provider('CSMHttp', CsmHttpProvider)
  .name;
  

CsmHttpProvider.$inject = ['$httpProvider'];
/*******************************************************************************
 * CSMHttp */
/**
 * This is a utility that utilizes the $http service to make ajax calls to the
 * server. When the $http promise is rejected, which happens when the response status
 * code is not in between 200 and 299, the status text will be checked for a certain
 * format that corresponds to the csm.web.FaultResponse class on the server.
 *
 * A utility class, ParsedResponse, will be instantiated to process the rejected
 * response and parse out the type, code, and message of the FaultResponse if
 * possible. This instance will be provided to the handleError method defined
 * on this provider and as a property (parsedResponse) of the response when the
 * error was not handled and is passed to the rejected promise handler.
 *
 * The provider can be configured with the following properties:
 *   handleError - Error handling method that will be injected with any defined
 *                 services as well as two additional parameters:
 *                    response       - Response object
 *                    ParsedResponse - ParsedResponse instance for handling FaultResponse
 *                 This is defaulted to handle the ParsedResponse
 *   setAppPath  - Root application path to prepend to request urls.
 *   startPage   - Location to redirect during restarts. Default is the location.origin
 *
 * TODO: This entire service is deprecated in favor of `HTTPService` which still
 * TODO: uses it. All uses of this service must be replaced with `HTTPService`.
 *******************************************************************************/
/* @ngInject */ function CsmHttpProvider($httpProvider) {
  // Root Application Path.
  var mAppPath;
  var mProvider = this;

  /*****************************************************************************
   * Class: ResponseStatus */
  /**
   * UPDATED:
   * This was initially a Utility class for parsing the response for a status,
   * code, and message from the <b>csm.web.FaultResponse</b>.
   *
   * However that logic and flow was changed with GR-1798 by moving all of
   * the parsing logic to the server side (CSMCommonCS\web\handlers\HTTPHandler.cs.)
   * We no longer send the fault all the way to the client to be parsed but instead
   * parse it on the server and send down a status code and message to indicate
   * what occurred.
   *
   * Ultimately this whole parsed response business should be removed, however it is
   * added onto the response and passed down to the consumer to be used so its not
   * as easily removable without a lot of touching.
   *
   * `returnAlertClosed` is a signal to `handleError` to set `alertClosed` to the
   * alert promise for modal alert dialogs.
   *
   * @param {object}  resp              `$http` response object.
   * @param {boolean} returnAlertClosed See above.
   *****************************************************************************/
  function ParsedResponse(resp, returnAlertClosed) {
    /**
     * !! Important
     * These status codes constants mirror the enum on the server located
     * in CSMCommonCS\CSMCommonCS\web\handlers\HTTPHandler.cs.
     * If you make a change here you must change in both places.
     */
    const STATUS_CODES = {
      UNKNOWN: 0,
      FAILED: 1,
      HTTP_ERROR: 2,
      SVC_ERROR: 3,
      SVC_FATAL: 4,
      SPROC_ERROR: 5,
      SPROC_FATAL: 6,
      SSN_TIMEOUT: 7,
      SQL_INVALID_ACCESS: 8,
      SQL_BUSY: 9
    };

    // True when modal alert promises should be returned.
    this.returnAlertClosed = returnAlertClosed;

    // Accessors
    this.isFailed = () => statusCode == STATUS_CODES.FAILED;
    this.isHttpError = () => statusCode == STATUS_CODES.HTTP_ERROR;
    this.isSvcError = () => statusCode == STATUS_CODES.SVC_ERROR;
    this.isSvcFatal = () => statusCode == STATUS_CODES.SVC_FATAL;
    this.isSpError = () => statusCode == STATUS_CODES.SPROC_ERROR;
    this.isSpFatal = () => statusCode == STATUS_CODES.SPROC_FATAL;
    this.isTimeout = () => statusCode == STATUS_CODES.SSN_TIMEOUT;
    this.isInvalidAccess = () =>
      statusCode == STATUS_CODES.SQL_INVALID_ACCESS;
    this.isSqlBusy = () => statusCode == STATUS_CODES.SQL_BUSY;
    this.message = () => errorMessage;
    this.code = () => statusCode;

    /**
     * See CSMCommonCS\CSMCommonCS\web\handlers\HTTPHandler.cs for
     * application of the X status code header.
     *
     * Basically it tells us what kind of error occurred on the server
     * and based on the type of error, we chose the appropriate action
     * to take - for example on some we log them out and other we just
     * give them data. See handleError below.
     *
     * Also see link for docs on angular's $http response object
     * https://docs.angularjs.org/api/ng/service/$http
     * resp.headers() returns null if nothing is found.
     **/
    let statusCode = resp.headers('X-Status-Code');
    let serverMessage = resp.headers('X-Status-Message');
    /**
     * Previously the errorMessage was just looking at the resp.statusText 
     * however with the WAF, it sanitizes that field specifically which 
     * was preventing us from sending down our messages properly.
     * I have chosen to remove the resp.statusText as a fall back as this 
     * could lead to us coding locally one way that may in fact later be 
     * blocked by the WAF in prod.
     */
    let errorMessage = serverMessage || '';
    /**
     * It's ugly - I know.
     * In an effort to maintain backwards compat, we are going to 
     * reassign the resp.statusText property with the server message
     * similar to how it is being done for handling 418 status codes
     * within remoting/HTTPService.js
     */
    resp.statusText = errorMessage;

    /* Unhandled Exception */
    if (!statusCode) {
      // 451 is authorization failure/authorization code mismatch.
      if (resp.status == 451) statusCode = `${STATUS_CODES.SSN_TIMEOUT}`;
      // 500 = unhandled exception on server.
      else if (resp.status == 500) statusCode = `${STATUS_CODES.SVC_FATAL}`;
      /**
       * <0 or >500 = failed to reach server.
       * All others, let the caller handler it.
       */ else if (resp.status < 0 || resp.status > 500) {
        statusCode = `${STATUS_CODES.FAILED}`;
        errorMessage = resp.data;
      }
    }
  }

  /*****************************************************************************
   * handleError */
  /**
   * This function is called for a response error and returns `true` if the error
   * was handled. Standard errors are handled by notifying the user by either a
   * non-modal notification or an alert dialog.
   *
   * The function will be injected with any necessary services as well as a
   * ParsedResponse object to process FaultResponse exceptions from CSMCommonCS.
   * This method can be overridden during application configuration.
   *
   * If `ParsedResponse.returnAlertClosed` is `true`, the alert modal dialog
   * promise, if any, is is returned in `ParsedResponse.alertClosed` and resolved
   * when the alert closes. If a fatal alert, the promise is resolved with the
   * application start page URL. `CallSvc` returns this property so the caller
   * can handle the dialog closure.
   *
   * If `ParsedResponse.returnAlertClosed` is `false` (default), fatal alert
   * dialogs set `window.location` to the application start page URL when the
   * dialog closes.
   *
   * @return Boolean indication of error handling
   *****************************************************************************/
  /* @ngInject */
  mProvider.handleError = function (Alert, BusyScreen, ParsedResponse) {
    if (ParsedResponse.isTimeout()) {
      ParsedResponse.alertClosed = Alert.error(
        'Due to inactivity and for your protection you have been logged out. Please login again.',
        'Inactivity Alert',
        true
      ).finally(resetToStart);
    } else if (ParsedResponse.isSqlBusy()) {
      Alert.info(
        'The server is currently busy and can not process your request, please try again.',
        'Server busy'
      );
    } else if (ParsedResponse.isFailed()) {
      /* Didn't reach the server (Unknown error). */
      Alert.info(
        "We're having trouble reaching the server. Please try again.",
        'Server Could Not Be Reached'
      );
    } else if (
      /* Fatal errors show an alert and abort to a fresh instance */
      ParsedResponse.isHttpError() ||
      ParsedResponse.isSvcFatal() ||
      ParsedResponse.isSpFatal()
    ) {
      ParsedResponse.alertClosed = Alert.error(
        "An error has occurred. We're sorry for the inconvenience.",
        'Problem with Server',
        true
      ).finally(resetToStart);
    } else if (ParsedResponse.isInvalidAccess()) {
      /* Unauthorized service access */
      ParsedResponse.alertClosed = Alert.error(
        'You do no have access to this part of the application. If you think you got this in error, ' +
          'please contact customer support.',
        'Unauthorized Access',
        true
      ).finally(resetToStart);
    } else {
      /* The error wasn't handled here, and will be picked up by any error listeners */
      return false;
    }

    /**
     * Since the error was handled here, we need
     * to clear the busy screen if it currently up
     */
    BusyScreen.setBusy(false);

    // Return true to indicate the error has been handled
    return true;

    /*****************************************************************************
     * resetToStart */
    /**
     * Set the window location to the start page or the origin(default). If
     * `ParsedResponse.returnAlertClosed` is `true`, a promise resolved with the
     * start page URL is returned.
     *****************************************************************************/
    function resetToStart() {
      var url = mProvider.startPage || '/';

      if (ParsedResponse.returnAlertClosed) return $q.resolve(url);
      else window.location.href = url;
    }
  };

  /*****************************************************************************
   * setAppPath */
  /**
   * Sets the root application request path.
   *
   * @param path  String path to set
   *****************************************************************************/
  /* @ngInject */
  mProvider.setAppPath = function (path) {
    mAppPath = path;
  };

  /*--------------------------------------------------*/
  /* Returns wrapper object for making service calls. */
  /*--------------------------------------------------*/
  /* @ngInject */
  mProvider.$get = function ($q, $http, $injector, TemplateTextBuilder) {
    /***************************************************************************
     * Constants
     ***************************************************************************/
    /*--------------*/
    /* Method Types */
    /*--------------*/
    var METHOD_GET = 'GET';
    var METHOD_POST = 'POST';

    /** Default response transform which handles JSON deserialization */
    var defaultTransformResponse = $http.defaults.transformResponse;

    /***************************************************************************
     * callSvc */
    /**
     * Calls a RESTful service with `$http`. `data` is passed in the query string
     * for a GET request and in the payload for a POST. Returns the promise from
     * `$http` for successful responses.
     *
     * Error responses are passed through `ParsedResponse` and processed through
     * `handleError`. If `handleError` returns `true`, meaning the error has been
     * handled and requires nothing further, the returned promise is not rejected
     * and becomes an orphan. If `handleError` returns `false`, or `rejectAllFails`
     * is `true`, the returned promise is rejected with an object containing:
     *
     *   - `{ParsedResponse}`**`parsedResponse`**
     *     Object created from `$http` response.
     *
     *   - `{boolean}`**`handled`**
     *     `true` when response handled by `handleError`.
     *
     *   - `{promise}`**`alertClosed`** (if `rejectAllFails` is `true`, see below)
     *     Promise from Alert message dialog, if any. Resolved when the dialog
     *     with the URL of application start page, for fatal errors, or `null`.
     *
     * `rejectAllFails` rejects the returned promise even for failures that are
     * handled by the `errorHandler`. This is to allow for calls that need to
     * know of a failure even when the user has been notified. The object
     * returned in the rejected promise includes **`alertClosed`**.
     *
     * `ignoreAppPath` is a hack to allow for `HTTPService`, which uses
     * `Session.getSessionURL()` to apply the session ID to the URL, to use this
     * service without applying the app path twice.
     *
     * @param  path           URL to access. appRoot is prepended if it exists
     * @param  type           Method type (POST, GET, etc.)
     * @param  data           Data to send
     * @param  transform      Optional response transform method
     * @param  rejectAllFails true=Reject promise for handled failures. See above.
     * @param  ignoreAppPath  true=Ignore `mAppPath`. See above.
     * @return                Promise to listen for success/reject of call
     ***************************************************************************/
    function callSvc(
      path,
      type,
      data,
      transform,
      rejectAllFails,
      ignoreAppPath
    ) {
      var defer = $q.defer();
      var isGet = type == METHOD_GET;

      /*-----------------------------------------------------------------*/
      /* In order to defeat IE browser caching for get requests, a time  */
      /* stamp is added to the querystring to ensure unique request urls */
      /*-----------------------------------------------------------------*/
      var getData = isGet
        ? angular.extend({}, data, { ts: new Date().getTime() })
        : '';

      /*----------------------------------------------------------*/
      /* For IE, numbers cannot be posted directly and will cause */
      /* an 'Invalid Argument' error. This will convert numeric   */
      /* data to strings when not NaN for IE to play nice.        */
      /*----------------------------------------------------------*/
      var postData = isGet
        ? ''
        : angular.isNumber(data)
          ? !isNaN(data)
            ? data.toString()
            : null
          : data;
      /*------------------*/
      /* Send the request */
      /*------------------*/
      var options = {
        method: type,
        url: !!ignoreAppPath ? path : (mAppPath || '') + path,
        params: getData,
        data: postData,
        transformResponse: transform
          ? defaultTransformResponse.concat(transform)
          : defaultTransformResponse
      };

      $http(options)
        .then(defer.resolve)

        /*--------------------------------------------------------------------*/
        /* Parse the error response and pass to the error handler. Reject the */
        /* promise if the error was not handled or `rejectAllFails` is true.  */
        /*--------------------------------------------------------------------*/
        .catch(function (response) {
          var pr = new ParsedResponse(response, rejectAllFails);
          var handled = $injector.invoke(mProvider.handleError, null, {
            response: response,
            ParsedResponse: pr
          });

          if (!handled || rejectAllFails) {
            response.parsedResponse = pr;
            response.handled = handled;
            response.alertClosed = pr.alertClosed;
            defer.reject(response);
          }
        });

      return defer.promise;
    }

    return {
      callSvc: callSvc,

      /*--------------------------------------------------------------------*/
      /* TODO: Do not use these methods as they are all deprecated in favor */
      /* TODO: of the `HTTPService` and using `String.format` to apply      */
      /* TODO: parameters to `PATH_INFO` rather than `TemplateTextBuilder`. */
      /*--------------------------------------------------------------------*/
      doGet: function (path, data, transform) {
        return this.get(path, data, transform);
      },
      doPost: function (path, data, transform) {
        return this.post(path, data, transform);
      },
      get: function (path, data, transform) {
        return callSvc(path, METHOD_GET, data, transform);
      },
      post: function (path, data, transform) {
        return callSvc(path, METHOD_POST, data, transform);
      },
      getAppPath: function () {
        return mAppPath;
      },

      routing: {
        get: function (route, parms, data, transform) {
          return this.call(METHOD_GET, route, parms, data, transform);
        },
        post: function (route, parms, data, transform) {
          return this.call(METHOD_POST, route, parms, data, transform);
        },
        call: function (method, route, parms, data, transform) {
          var path = !!parms
            ? new TemplateTextBuilder(route, parms).toString()
            : route;
          return callSvc(path, method, data, transform);
        }
      }
    };
  };
}
